/**
 * Utility to transform InfertilityState to API format
 */

import type { InfertilityState } from "./InfertilityContext";
import type { Patient } from "@src/packages/patients/patients.types";
import { RequestBody } from "@src/packages/openapi/helpers";
import { paths } from "@src/packages/openapi/schema";

type PatientPatch = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;
type InfertilityHistoryPatch = NonNullable<
  NonNullable<PatientPatch["fertilityHistory"]>["infertilityHistory"]
>;
type InfertilityCausePayload = NonNullable<
  NonNullable<InfertilityHistoryPatch["causes"]>[number]
>;

export function transformInfertilityStateToAPI(
  state: InfertilityState,
  partnerCause?: string | null,
  partnerComments?: string | null,
  patient?: Patient | null, // Pass patient to get existing cause IDs
): PatientPatch {
  // Map hasDiagnosis to API booleans
  // "art_needed" = "No but I need ART for a genetic disease" (ART = Assisted Reproductive Technology)
  const isDiagnosedWithInfertility = state.hasDiagnosis === "yes";
  const isARTNeededForCarrierDisease = state.hasDiagnosis === "art_needed";

  // Get existing infertility history + causes from DB
  const existingInfertilityHistory =
    patient?.fertilityHistory?.infertilityHistory ?? null;
  const existingCauses = existingInfertilityHistory?.causes ?? [];

  // Some mobile payloads may not include infertilityHistory.id even when
  // causes already exist. Infer the infertilityHistoryId from the first cause
  // as a fallback so that the backend uses UPDATE instead of CREATE.
  const existingInfertilityHistoryId: string | undefined =
    existingInfertilityHistory?.id ??
    (existingCauses.length > 0
      ? existingCauses[0]?.infertilityHistoryId
      : undefined);

  // Build causes array: include selected causes and deletions
  const causes: InfertilityCausePayload[] = [];

  // If hasDiagnosis is "no" or "art_needed", delete all existing causes
  if (state.hasDiagnosis === "no" || state.hasDiagnosis === "art_needed") {
    if (existingInfertilityHistoryId) {
      // Only send delete ops when we know there is an existing history;
      // otherwise the backend would treat them as creates.
      existingCauses.forEach((existingCause) => {
        if (existingCause.id) {
          causes.push({
            id: existingCause.id,
            op: "delete" as const,
          });
        }
      });
    }
  } else if (isDiagnosedWithInfertility) {
    // User is diagnosed with infertility - handle causes
    const selectedCauses = new Set(state.causes ?? []);

    // Backend logic for update mode (when infertilityHistory.id exists):
    // - cause.id && op === "delete" -> deleteInfertilityCauseHistory
    // - cause.id && no op          -> updateInfertilityCauseHistory (now fixed to exclude id)
    // - no id                      -> createInfertilityCauseHistory
    //
    // Backend logic for create mode (when no infertilityHistory.id):
    // - op is ignored, all items are treated as creates
    // So we MUST NOT send delete ops when there is no existing history.

    const hasExistingInfertilityHistoryId = !!existingInfertilityHistoryId;

    if (hasExistingInfertilityHistoryId) {
      // First, mark causes for deletion that exist in DB but aren't selected
      existingCauses.forEach((existingCause) => {
        if (existingCause.id && !selectedCauses.has(existingCause.cause)) {
          causes.push({
            id: existingCause.id,
            op: "delete" as const,
          });
        }
      });
    }

    // Then, add selected causes
    if (state.causes && state.causes.length > 0) {
      state.causes.forEach((cause) => {
        if (hasExistingInfertilityHistoryId) {
          // UPDATE mode: if a cause with this type already exists, update it by id
          const existingCause = existingCauses.find((c) => c.cause === cause);
          if (existingCause?.id) {
            // Update existing cause - backend now properly excludes id from update
            causes.push({
              id: existingCause.id,
              cause,
              other: cause === "other" ? state.causeOther : undefined,
            });
            return;
          }
        }

        // CREATE mode or no existing cause of this type: create new (no id)
        causes.push({
          cause,
          other: cause === "other" ? state.causeOther : undefined,
        });
      });
    }
  }
  // If hasDiagnosis is null/undefined, don't send causes (preserve existing)

  // Handle partner infertility fields
  // If partner is not infertile, clear cause and comments
  const isPartnerInfertile = state.partnerHasDiagnosis ?? undefined;
  const partnerInfertilityCause =
    isPartnerInfertile === false ? null : partnerCause || undefined;
  const partnerNotes =
    isPartnerInfertile === undefined
      ? undefined
      : isPartnerInfertile === false
        ? null
        : partnerComments || null;

  return {
    fertilityHistory: {
      infertilityHistory: {
        // IMPORTANT: include existing infertilityHistory.id when present
        // so backend uses updateInfertilityHistory (and respects cause ops)
        id: existingInfertilityHistoryId,
        isDiagnosedWithInfertility,
        isARTNeededForCarrierDisease,
        causes: causes.length > 0 ? causes : undefined,
        isPartnerInfertile,
        partnerInfertilityCause,
        notes: partnerNotes,
      },
    },
  };
}
